import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Plus, RotateCcw, X } from 'lucide-react';
import { useSetAtom } from 'jotai';
import type { Report } from 'powerbi-client';
import type { SavedView } from '../../interfaces/SavedView';
import { ToastVariant } from '../../constants/toast-variant';
import { PowerBiListView } from './power-bi-list-view';
import { PowerBiCaptureDialog } from './power-bi-capture-dialog';
import { PowerBiUpdateView } from './power-bi-update-view';
import { PowerBiDeleteView } from './power-bi-delete-view';
import { PowerBiExportView } from './power-bi-export-view';
import styles from '../power-bi/power-bi-page.module.scss';
import { toastAtom } from '../../atoms/toastAtom';
import { useMimic } from '../../hooks/useMimic';

interface PowerBiToolbarProps {
  appName: string;
  report: Report | null;
  views: SavedView[];
  viewsLoading: boolean;
  embedReady: boolean;
  onCaptureView: (name: string) => Promise<void>;
  onApplyView: (base64Embedding: string) => Promise<void>;
  onDeleteView: (viewId: string) => Promise<void>;
  onDeleteSuccess?: () => void;
  onExitView: () => Promise<void>;
  onUpdateView: (viewId: string, viewName?: string) => Promise<void>;
}

export function PowerBiToolbar({
  appName,
  report,
  views,
  viewsLoading,
  embedReady,
  onCaptureView,
  onApplyView,
  onDeleteView,
  onDeleteSuccess,
  onExitView,
  onUpdateView,
}: PowerBiToolbarProps) {
  const [showCaptureDialog, setShowCaptureDialog] = useState(false);
  const [activeView, setActiveView] = useState<SavedView | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [committedName, setCommittedName] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const setToast = useSetAtom(toastAtom);
  const { isMimic, mimicUser } = useMimic();

  useEffect(() => {
    if (isEditingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [isEditingName]);

  const displayName = committedName ?? activeView?.viewName ?? '';

  const handleStartEditing = () => {
    setEditNameValue(displayName);
    setIsEditingName(true);
  };

  const handleCommitName = () => {
    const trimmed = editNameValue.trim();
    if (trimmed && trimmed !== activeView?.viewName) {
      setCommittedName(trimmed);
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCommitName();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleApplyView = async (view: SavedView) => {
    try {
      await onApplyView(view.base64Embedding);
      setActiveView(view);
      setCommittedName(null);
      setIsEditingName(false);
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to apply view. Please try again.',
        variant: ToastVariant.ERROR,
      });
      throw err;
    }
  };

  const handleReturnToView = async () => {
    if (!activeView) return;
    try {
      await onApplyView(activeView.base64Embedding);
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to apply view. Please try again.',
        variant: ToastVariant.ERROR,
      });
    }
  };

  const handleSave = async (name: string) => {
    try {
      await onCaptureView(name);
      setToast({
        message: `"${name}" saved successfully.`,
        variant: ToastVariant.SUCCESS,
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to save view. Please try again.',
        variant: ToastVariant.ERROR,
      });
      throw err;
    }
  };

  const clearActiveView = () => {
    setActiveView(null);
    setCommittedName(null);
    setIsEditingName(false);
  };

  const handleDeleteSuccess = () => {
    clearActiveView();
    onDeleteSuccess?.();
  };

  const handleExitView = async () => {
    try {
      await onExitView();
      clearActiveView();
    } catch {
      setToast({
        message: 'Failed to exit view. Please try again.',
        variant: ToastVariant.ERROR,
      });
    }
  };

  const handleUpdateSuccess = () => {
    if (activeView && committedName) {
      setActiveView({ ...activeView, viewName: committedName });
    }
    setCommittedName(null);
  };

  return (
    <>
      <div className={styles['toolbar']}>
        <PowerBiListView
          views={views}
          viewsLoading={viewsLoading}
          activeViewId={activeView?.viewId ?? null}
          onApplyView={handleApplyView}
        />

        <button
          className={styles['toolbar-btn']}
          onClick={() => setShowCaptureDialog(true)}
          disabled={!embedReady}
        >
          <Plus size={14} aria-hidden="true" />
          Capture View
        </button>

        <PowerBiExportView
          appName={appName}
          report={report}
          embedReady={embedReady}
        />

        {activeView && (
          <div className={styles['active-view-edit']}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                className={styles['active-view-input']}
                value={editNameValue}
                onChange={(e) => setEditNameValue(e.target.value)}
                onBlur={handleCommitName}
                onKeyDown={handleNameKeyDown}
                aria-label="Edit view name"
              />
            ) : (
              <>
                <button
                  className={styles['active-view-name']}
                  onClick={handleReturnToView}
                  disabled={!embedReady}
                  title={`Return to "${displayName}"`}
                  aria-label={`Return to view ${displayName}`}
                >
                  {displayName}
                </button>
                <button
                  className={styles['toolbar-ghost-btn']}
                  onClick={handleStartEditing}
                  title="Rename view"
                  aria-label="Rename view"
                >
                  <Pencil size={12} aria-hidden="true" />
                </button>
                {committedName && (
                  <button
                    className={styles['toolbar-ghost-btn']}
                    onClick={() => setCommittedName(null)}
                    title={`Revert to "${activeView.viewName}"`}
                    aria-label="Revert view name"
                  >
                    <RotateCcw size={12} aria-hidden="true" />
                  </button>
                )}
                <button
                  className={styles['toolbar-ghost-btn']}
                  onClick={handleExitView}
                  disabled={!embedReady}
                  title="Exit view and return to the default report"
                  aria-label="Exit view"
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        )}

        <div style={{ flex: 1 }} />

        <PowerBiUpdateView
          activeView={activeView}
          pendingName={committedName}
          embedReady={embedReady}
          onUpdateView={onUpdateView}
          onUpdateSuccess={() => handleUpdateSuccess()}
        />
        <PowerBiDeleteView
          activeView={activeView}
          onDeleteView={onDeleteView}
          onDeleteSuccess={handleDeleteSuccess}
        />
      </div>

      {isEditingName && isMimic && mimicUser && (
        <p className={styles['mimic-notice']} role="status">
          You are managing Saved Views for {mimicUser.name} while operating in
          Mimic Mode.
        </p>
      )}

      <PowerBiCaptureDialog
        isOpen={showCaptureDialog}
        onClose={() => setShowCaptureDialog(false)}
        onSave={handleSave}
      />
    </>
  );
}
